import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiRequest, Notification, useAccount, useNotifications } from "@vojomo-core/shared";
import {
  Bell,
  Share2,
  MessageCircle,
  Sparkles,
  AlertTriangle
} from "lucide-react";
import {
  Button,
  Tabs,
  Pagination,
  Empty,
  Spin,
  Form,
  Input,
  Radio,
  Alert
} from "antd";

const LIMIT = 20;

export function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

const notificationIcon: Record<
  Notification["type"],
  React.ComponentType<{ size?: number; className?: string }>
> = {
  share: Share2,
  comment: MessageCircle,
  ai_complete: Sparkles,
  spending_limit: AlertTriangle,
  broadcast: Bell,
  workflow_assignment: Bell,
  workflow_artifact_ready: Sparkles
};

type Target = "all" | "company" | "user";

interface BroadcastFormProps {
  isVojomoAdmin: boolean;
  company: string;
}

function BroadcastForm({
  isVojomoAdmin,
  company
}: BroadcastFormProps): React.ReactElement {
  const [form] = Form.useForm();
  const [target, setTarget] = useState<Target>("all");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);

  const handleSubmit = async (values: {
    title: string;
    message: string;
    target: Target;
    target_value?: string;
  }) => {
    setIsSubmitting(true);
    setResult(null);
    try {
      const data = isVojomoAdmin
        ? await apiRequest<{ success: boolean; message: string }>({
            subRoute: "admin",
            path: "/notification/broadcast",
            method: "POST",
            body: {
              title: values.title,
              message: values.message,
              target: values.target,
              target_value: values.target_value ?? ""
            }
          })
        : await apiRequest<{ success: boolean; message: string }>({
            subRoute: "notification",
            path: "/company_broadcast",
            method: "POST",
            body: { title: values.title, message: values.message }
          });
      setResult(data);
      if (data.success) {
        form.resetFields();
        setTarget("all");
      }
    } catch {
      setResult({ success: false, message: "An unexpected error occurred." });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-lg pt-2">
      <p className="text-gray-500 mb-6 text-sm">
        {isVojomoAdmin
          ? "Send an in-app notification to all users, a specific company, or a specific user."
          : `Send an in-app notification to all users at ${company}.`}
      </p>

      {result && (
        <Alert
          className="mb-4"
          type={result.success ? "success" : "error"}
          message={result.message}
          showIcon
          closable
          onClose={() => setResult(null)}
        />
      )}

      <Form
        form={form}
        layout="vertical"
        initialValues={{ target: "all" }}
        onFinish={handleSubmit}
      >
        <Form.Item
          label="Title"
          name="title"
          rules={[{ required: true, message: "Title is required" }]}
        >
          <Input placeholder="e.g. New feature available!" maxLength={100} />
        </Form.Item>

        <Form.Item
          label="Message"
          name="message"
          rules={[{ required: true, message: "Message is required" }]}
        >
          <Input.TextArea
            placeholder="Notification body text..."
            rows={4}
            maxLength={500}
            showCount
          />
        </Form.Item>

        {isVojomoAdmin && (
          <>
            <Form.Item label="Send to" name="target">
              <Radio.Group
                onChange={(e) => setTarget(e.target.value)}
                value={target}
              >
                <Radio value="all">All users</Radio>
                <Radio value="company">Specific company</Radio>
                <Radio value="user">Specific user</Radio>
              </Radio.Group>
            </Form.Item>

            {target === "company" && (
              <Form.Item
                label="Company name"
                name="target_value"
                rules={[
                  { required: true, message: "Company name is required" }
                ]}
              >
                <Input placeholder="e.g. Acme Corp" />
              </Form.Item>
            )}

            {target === "user" && (
              <Form.Item
                label="User email"
                name="target_value"
                rules={[
                  { required: true, message: "User email is required" },
                  { type: "email", message: "Enter a valid email address" }
                ]}
              >
                <Input placeholder="user@example.com" />
              </Form.Item>
            )}
          </>
        )}

        {!isVojomoAdmin && (
          <Form.Item label="Sending to">
            <span className="text-gray-700 font-medium">{company}</span>
          </Form.Item>
        )}

        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            loading={isSubmitting}
            style={{ backgroundColor: "rgb(44, 21, 97)" }}
          >
            Send notification
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
}

export function Notifications(): React.ReactElement {
  const navigate = useNavigate();
  const { vojomoAccount } = useAccount();
  const { markAllRead: ctxMarkAllRead } = useNotifications();
  const isAdmin = vojomoAccount?.is_admin;
  const isCompanyAdmin = (vojomoAccount?.company_admin_companies?.length ?? 0) > 0;
  const company =
    vojomoAccount?.company_admin_companies?.[0] ?? vojomoAccount?.company ?? "";

  const [allNotifications, setAllNotifications] = useState<Notification[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [activeTab, setActiveTab] = useState<"all" | "unread" | "send">("all");
  const [isLoading, setIsLoading] = useState(false);
  const [isMarkingAll, setIsMarkingAll] = useState(false);

  const fetchHistory = useCallback(async (pageNum: number) => {
    setIsLoading(true);
    try {
      const data = await apiRequest<{
        notifications: Notification[];
        total: number;
        page: number;
        limit: number;
      }>({
        subRoute: "notification",
        path: `/history?page=${pageNum}&limit=${LIMIT}`
      });
      setAllNotifications(data.notifications);
      setTotal(data.total);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab !== "send") fetchHistory(page);
  }, [page, fetchHistory, activeTab]);

  const displayed =
    activeTab === "unread"
      ? allNotifications.filter((n) => !n.is_read)
      : allNotifications;

  const markAllRead = async () => {
    setIsMarkingAll(true);
    try {
      await ctxMarkAllRead();
      setAllNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
    } finally {
      setIsMarkingAll(false);
    }
  };

  const handleRowClick = (n: Notification) => {
    navigate(`/notifications/${n._id}`, { state: { notification: n } });
  };

  const unreadCount = allNotifications.filter((n) => !n.is_read).length;
  const isListTab = activeTab === "all" || activeTab === "unread";

  const tabItems = [
    { key: "all", label: "All" },
    {
      key: "unread",
      label: `Unread${unreadCount > 0 ? ` (${unreadCount})` : ""}`
    },
    ...((isAdmin || isCompanyAdmin) ? [{ key: "send", label: "Send" }] : [])
  ];

  return (
    <div className="w-full mx-auto py-6 px-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold m-0">Notifications</h2>
        {isListTab && unreadCount > 0 && (
          <Button loading={isMarkingAll} onClick={markAllRead}>
            Mark all as read
          </Button>
        )}
      </div>

      <Tabs
        activeKey={activeTab}
        onChange={(key) => {
          setActiveTab(key as typeof activeTab);
          setPage(1);
        }}
        items={tabItems}
      />

      {activeTab === "send" ? (
        <BroadcastForm isVojomoAdmin={!!isAdmin} company={company} />
      ) : isLoading ? (
        <div className="flex justify-center py-12">
          <Spin />
        </div>
      ) : displayed.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            activeTab === "unread"
              ? "No unread notifications"
              : "No notifications yet"
          }
          className="py-12"
        />
      ) : (
        <div className="flex flex-col gap-1">
          {displayed.map((n) => {
            const Icon = notificationIcon[n.type] ?? Bell;
            return (
              <div
                key={n._id}
                onClick={() => handleRowClick(n)}
                className={`flex gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                  n.is_read
                    ? "border-gray-100 bg-white hover:bg-gray-50 opacity-70"
                    : "border-purple-100 bg-purple-50 hover:bg-purple-100"
                }`}
              >
                <div className="mt-0.5 shrink-0">
                  <Icon size={18} className="text-purple-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm leading-snug">
                    {n.title}
                  </p>
                  <p className="text-sm text-gray-600 leading-snug">
                    {n.message}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    {timeAgo(n.created_at)}
                  </p>
                </div>
                {!n.is_read && (
                  <div className="w-2.5 h-2.5 rounded-full bg-purple-600 mt-1.5 shrink-0" />
                )}
              </div>
            );
          })}
        </div>
      )}

      {isListTab && !isLoading && activeTab === "all" && total > LIMIT && (
        <div className="flex justify-center mt-6">
          <Pagination
            current={page}
            total={total}
            pageSize={LIMIT}
            onChange={setPage}
            showSizeChanger={false}
          />
        </div>
      )}
    </div>
  );
}
